import React from 'react';
import PropTypes from 'prop-types';
import { mergeStyles, GlobalContext } from 'wix-rich-content-common';
import classNames from 'classnames';
import { generateInsertPluginButtonProps } from '../Utils/generateInsertPluginButtonProps.js';
import { FileInput } from 'wix-rich-content-ui-components';
import { BUTTON_TYPES, ToolbarButton } from 'wix-rich-content-editor-common';
import { s as styleInject } from '../style-inject.es-746bb8ed.js';
import '../Utils/getPluginParams.js';

var css_248z = "._7nMYw{align-items:center;color:var(--ricos-fallback-color,#000);display:flex;flex-direction:column;height:100%;justify-content:center;pointer-events:auto;width:100%}[dir] ._7nMYw{background:transparent;border:0;cursor:pointer}._7nMYw._59l5O{color:#646464}._7nMYw._59l5O:not([disabled]){color:var(--ricos-text-color,#212121)}._7nMYw._59l5O:focus:not([disabled]) .MkYfn,._7nMYw._59l5O:hover:not([disabled]) .MkYfn{color:var(--ricos-settings-action-color,var(--ricos-action-color,#0261ff))}[dir] ._7nMYw._59l5O:focus:not([disabled]) .MkYfn,[dir] ._7nMYw._59l5O:hover:not([disabled]) .MkYfn{cursor:pointer}._7nMYw._59l5O:focus:not([disabled]) .MkYfn svg,._7nMYw._59l5O:hover:not([disabled]) .MkYfn svg{fill:var(--ricos-settings-icons-color,currentColor);color:var(--ricos-settings-icons-color,currentColor)}._7nMYw.bzmt0{opacity:.4}[dir] ._7nMYw.bzmt0{cursor:default}.MkYfn{height:20px;width:20px}.MkYfn svg{fill:var(--ricos-settings-icons-color,currentColor);color:var(--ricos-settings-icons-color,currentColor);height:100%;width:100%}.XMVU3{font-size:12px}[dir] .XMVU3{margin:8px 0 0}.YJLt6,.grDci ._7nMYw{height:100%;width:100%}.grDci ._7nMYw{box-sizing:border-box}[dir] .grDci ._7nMYw{padding:22px 0}.grDci ._7nMYw:focus:not([disabled]) .MkYfn{color:var(--ricos-settings-action-color,var(--ricos-action-color-fallback,#0261ff))}[dir] .grDci ._7nMYw:focus:not([disabled]) .MkYfn{cursor:pointer}.grDci ._7nMYw:focus:not([disabled]) .MkYfn svg{fill:var(--ricos-settings-icons-color,currentColor);color:var(--ricos-settings-icons-color,currentColor)}.grDci .MkYfn{display:block;height:25px;width:25px}[dir] .grDci .MkYfn{margin:0 auto}.grDci .XMVU3{display:block;font-size:13px;line-height:21px}[dir] .grDci .XMVU3{margin:6px 0 0}";
var styles = {"button":"_7nMYw","footerToolbarButton":"_59l5O","icon":"MkYfn","forceDisabled":"bzmt0","label":"XMVU3","buttonWrapper":"YJLt6","mobile":"grDci"};
styleInject(css_248z);

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
const createBaseInsertPluginButton = ({
  blockType,
  button,
  helpers,
  pubsub,
  commonPubsub,
  settings,
  t,
  theme,
  isMobile,
  pluginDefaults
}) => {
  var _a;
  return _a = class extends React.PureComponent {
    constructor(props) {
      super(props);
      __publicField(this, "getButtonProps", () => {
        const { setEditorState, getEditorState, closePluginMenu, pluginMenuButtonRef } = this.props;
        const { experiments } = this.context;
        return generateInsertPluginButtonProps({
          blockType,
          button,
          helpers,
          pubsub,
          commonPubsub,
          settings,
          t,
          theme,
          isMobile,
          pluginDefaults,
          getEditorState,
          setEditorState,
          toolbarName: this.toolbarName,
          closePluginMenu,
          pluginMenuButtonRef,
          experiments
        });
      });
      __publicField(this, "renderButton", ({ getIcon, getLabel, onClick, dataHook, isDisabled, tooltip }) => {
        const { styles: styles2 } = this;
        const { className, showName, tabIndex, forceDisabled, sideToolbar } = this.props;
        const Icon = getIcon();
        const label = getLabel();
        return /* @__PURE__ */ React.createElement(
          "button",
          {
            disabled: isDisabled() || forceDisabled,
            "aria-label": tooltip,
            tabIndex,
            className: classNames(
              className,
              styles2.button,
              sideToolbar ? styles2.sideToolbarButton : styles2.footerToolbarButton,
              { [styles2.forceDisabled]: isDisabled() || forceDisabled }
            ),
            "data-hook": dataHook,
            onClick,
            ref: this.buttonRef
          },
          /* @__PURE__ */ React.createElement("div", { className: styles2.icon }, /* @__PURE__ */ React.createElement(Icon, { key: "0" })),
          showName && /* @__PURE__ */ React.createElement("span", { key: "1", className: styles2.label }, label)
        );
      });
      __publicField(this, "renderFileUploadButton", ({
        getIcon,
        getLabel,
        onChange,
        accept,
        multiple,
        dataHook,
        isDisabled
      }) => {
        const { showName, tabIndex, forceDisabled, sideToolbar, className } = this.props;
        const { styles: styles2 } = this;
        const Icon = getIcon();
        const label = getLabel();
        return /* @__PURE__ */ React.createElement(
          FileInput,
          {
            disabled: isDisabled() || forceDisabled,
            dataHook: `${dataHook}_file_input`,
            className: classNames(
              className,
              styles2.button,
              sideToolbar ? styles2.sideToolbarButton : styles2.footerToolbarButton,
              { [styles2.forceDisabled]: isDisabled() || forceDisabled }
            ),
            onChange,
            accept,
            multiple,
            theme: this.props.theme,
            tabIndex
          },
          /* @__PURE__ */ React.createElement("div", { className: styles2.icon }, /* @__PURE__ */ React.createElement(Icon, { key: "0" })),
          showName && /* @__PURE__ */ React.createElement("span", { key: "1", className: styles2.label }, label)
        );
      });
      const { buttonStyles } = props.theme || {};
      this.state = { isVisible: !(button == null ? void 0 : button.isVisiblePromise) };
      this.styles = mergeStyles({ styles, theme: buttonStyles });
      this.buttonRef = React.createRef();
      this.toolbarName = props.toolbarName;
    }
    componentDidMount() {
      var _a2;
      const { onButtonVisible } = this.props;
      if (button == null ? void 0 : button.isVisiblePromise) {
        (_a2 = button.isVisiblePromise) == null ? void 0 : _a2.then((isVisible) => {
          if (isVisible) {
            onButtonVisible == null ? void 0 : onButtonVisible();
            this.setState({ isVisible });
          }
        });
      } else {
        onButtonVisible == null ? void 0 : onButtonVisible();
      }
    }
    render() {
      const { styles: styles2 } = this;
      const { isVisible } = this.state;
      if (!isVisible) {
        return null;
      }
      const { theme: theme2, isMobile: isMobile2, forceDisabled } = this.props;
      const buttonProps = this.getButtonProps();
      const buttonWrapperClassNames = classNames(styles2.buttonWrapper, {
        [styles2.mobile]: isMobile2
      });
      const buttonRenderer = buttonProps.type === BUTTON_TYPES.FILE ? this.renderFileUploadButton : this.renderButton;
      const Button = /* @__PURE__ */ React.createElement("div", { className: buttonWrapperClassNames }, buttonRenderer(buttonProps));
      return /* @__PURE__ */ React.createElement(
        ToolbarButton,
        {
          theme: theme2,
          tooltipText: !forceDisabled && buttonProps.tooltip,
          button: Button,
          tooltipOffset: { y: 0 }
        }
      );
    }
  }, __publicField(_a, "propTypes", {
    className: PropTypes.string,
    onButtonVisible: PropTypes.func,
    getEditorState: PropTypes.func.isRequired,
    setEditorState: PropTypes.func.isRequired,
    theme: PropTypes.object,
    showName: PropTypes.bool,
    isMobile: PropTypes.bool,
    t: PropTypes.func,
    tabIndex: PropTypes.number,
    toolbarName: PropTypes.string,
    closePluginMenu: PropTypes.func,
    pluginMenuButtonRef: PropTypes.any,
    forceDisabled: PropTypes.bool,
    sideToolbar: PropTypes.bool
  }), __publicField(_a, "contextType", GlobalContext), _a;
};

export { createBaseInsertPluginButton };
//# sourceMappingURL=createBaseInsertPluginButton.js.map
